import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  PageContainer,
  Card,
  Button,
  SearchInput,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn,
  formatDate
} from '../styles/PageStyles';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { faCogs, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { getTools } from '../services/toolService';
import { getPipelines } from '../services/pipelineService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Styled components for the tools page

const ToolTags = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const ToolTag = styled.div`
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  background: rgba(0, 102, 255, 0.05);
  color: var(--primary);
`;


const ToolMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const ToolMetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToolCode = styled.pre`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
  font-family: 'Roboto Mono', monospace;
  font-size: 0.85rem;
  overflow-x: auto;
  margin-bottom: 1.5rem;
  border: 1px solid #eaeaea;
`;

const ToolFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const ToolCard = styled(Card)`
  margin-bottom: 1.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const ToolHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ToolTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToolTypeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.type === 'tool' ? '#fff5e6' : '#e3f2fd'};
  color: ${props => props.type === 'tool' ? '#ff9500' : '#1976d2'};
  border: 1px solid ${props => props.type === 'tool' ? '#ffcc80' : '#bbdefb'};
`;

const ToolStatus = styled.div`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  font-size: 0.85rem;
  color: var(--text-primary);
  background: white;  
  margin-left:5px;
`;

const ToolDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;


const ToolActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? 'var(--primary)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : '#e0e0e0'};
  border-radius: 20px;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary)' : 'rgba(0, 102, 255, 0.05)'};
  }
`;

const Tools = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [tools, setTools] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    try {
      const fetchTools = async () => {
        const filter = {};
        const sort = {};
        const query = { skip: 0, limit: 100 };
        const resp = await getTools(filter, sort, query);
        console.log("resp of tools ", resp)
        if (resp && resp.length) {
          setTools(resp);
        }
      }
      fetchTools();
    } catch (error) {
      console.log("Error getting the tools")
    }
  }, [])


console.log("Tools from server", tools)


  const openMangeTools = (id) => {
    const toolId = id ? id : 'new';
    navigate(`/tools/${toolId}`);
  }

  // Filter tools based on search term and active filter
  const filteredTools = tools.filter(tool => {
    const matchesSearch =
      (tool.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    if (activeFilter === 'all') return matchesSearch;
    const type = (tool.tool_type || tool.type || '').toLowerCase();
    return matchesSearch && (
      (activeFilter === 'api' && (type === 'api' || type === 'mcp_internal' || type === 'mcp_external')) ||
      (activeFilter === 'code' && type === 'code execution') ||
      (activeFilter === 'agent' && type === 'agent') ||
      (activeFilter === 'webhook' && type === 'webhook') ||
      (activeFilter === 'ml' && type === 'ml model') ||
      (activeFilter === 'email' && type === 'email')
    );
  });

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faScrewdriverWrench}
          title="Agent Tools"
          description="Tools are reusable components that agents can call to perform specific tasks within pipelines.
          They include APIs, code execution, ML models, webhooks, and connections to other agents."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <SearchInput
              type="text"
              placeholder="Search tools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '300px' }}
            />
            <Button onClick={() => openMangeTools()} primary>Create Tool</Button>
          </div>
        </PageHeaderWithInfo>


        <FilterBar>
          <FilterButton active={activeFilter === 'all'} onClick={() => setActiveFilter('all')}>All Types</FilterButton>
          <FilterButton active={activeFilter === 'api'} onClick={() => setActiveFilter('api')}>API</FilterButton>
          <FilterButton active={activeFilter === 'code'} onClick={() => setActiveFilter('code')}>Code Execution</FilterButton>
          <FilterButton active={activeFilter === 'agent'} onClick={() => setActiveFilter('agent')}>Agent</FilterButton>
          <FilterButton active={activeFilter === 'webhook'} onClick={() => setActiveFilter('webhook')}>Webhook</FilterButton>
          <FilterButton active={activeFilter === 'ml'} onClick={() => setActiveFilter('ml')}>ML Model</FilterButton>
          <FilterButton active={activeFilter === 'email'} onClick={() => setActiveFilter('email')}>Email</FilterButton>
        </FilterBar>

        <TabContainer>
          <TabList>
            <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>All Tools</Tab>
            <Tab active={activeTab === 'document'} onClick={() => setActiveTab('document')}>Document Processing</Tab>
            <Tab active={activeTab === 'integration'} onClick={() => setActiveTab('integration')}>Integration</Tab>
            <Tab active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>Analytics</Tab>
          </TabList>

          <TabContent active={true}>
            { filteredTools.length > 0 &&
                     filteredTools.map(tool => (
                       <ToolCard key={tool.id}>
                         <ToolHeader>
                           <ToolTitle>
                             <ToolTypeIndicator type={tool.tool_type || tool.type || 'tool'}>
                               <FontAwesomeIcon
                                 icon={faCogs} 
                               />
                               {tool.tool_type || tool.type || 'Tool'}
                             </ToolTypeIndicator>
                             {tool.name}
                             <ToolStatus active={true}>
                               Active
                             </ToolStatus>
                           </ToolTitle>
                           {/* <AgentsList>
                             {renderAgentsForPipeline(tool)}
                           </AgentsList> */}
                         </ToolHeader>
           
                         <ToolDescription>
                           {tool.description}
                         </ToolDescription>
           
                         <ToolFooter>
                             <div style={{ display: 'flex', gap: '2rem' }}>
                               <div>Last updated: {formatDate(tool.updated_at)} By {tool.updated_by}</div>
                               {tool.created_by && <div>Owner: {tool.created_by}</div>}
                               {tool?.code && <div>Code: {tool.code}</div>}
                               <div>Version: {tool.version || 1}</div>
                             </div>
            
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button onClick={() => openMangeTools(tool.id)} primary>Edit</Button>
                            </div>
                          </ToolFooter>
                       </ToolCard>
                     ))}
          </TabContent>
        </TabContainer>
      </motion.div>
    </PageContainer>
  );
};

export default Tools;
